import React, { useMemo, useState, useCallback } from "react";
import "./MaterialCalculator.css";

/* ==========================================================================
   Calculadora de Material — Decototal
   Refactor: claridad de flujo, validación por pasos y mejor manejo de errores
   de HubSpot. Diseño y comportamiento visual sin cambios.
   ========================================================================== */

// ---------------------------------------------------------------------------
// Datos de producto
// ---------------------------------------------------------------------------
const PRODUCTS = [
  { id: "spc-luxury", name: "Pisos SPC Luxury", category: "Pisos", unit: "Cajas", unitSingular: "caja", coverage: 2.9, note: "Cada caja de SPC Luxury cubre 2.9 m² (13 piezas de 1.22m x 18cm x 4.5mm).", hue: 50 },
  { id: "deck-tablon-wpc", name: "Deck Tablón WPC", category: "Deck", unit: "Piezas", unitSingular: "pieza", coverage: 0.406, note: "Cada pieza de Deck WPC cubre 0.406 m² (2.90m x 14cm x 1.5cm).", hue: 20 },
  { id: "lvt", name: "Piso Vinílico LVT", category: "Pisos", unit: "Cajas", unitSingular: "caja", coverage: 2.4, note: "Cada caja de LVT cubre 2.4 m² (18 piezas de 91cm x 15cm, sin pérdida por empalme).", hue: 60 },
  { id: "piso-deck-sintetico", name: "Piso Deck Sintético", category: "Deck", unit: "Piezas", unitSingular: "pieza", coverage: 0.406, note: "Cada pieza de Piso Deck Sintético cubre 0.406 m² (2.90m x 14cm).", hue: 15 },
  { id: "lambrin-concavo", name: "Muro Lambrín Cóncavo", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 0.551, orientable: true, pieceLength: 2.9, usefulWidth: 0.19, note: "Cada pieza de Lambrín Cóncavo cubre 0.551 m² (2.90m x 19cm, sin pérdida por empalme).", hue: 200 },
  { id: "bamboo-espejo", name: "Muro Bamboo Espejo", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 3.416, orientable: true, pieceLength: 2.8, usefulWidth: 1.22, note: "Cada pieza de Bamboo Espejo cubre 3.416 m² (1.22m x 2.80m, sin reutilizar recortes).", hue: 90 },
  { id: "placa-marmol", name: "Muro Placa Tipo Mármol", category: "Muro", unit: "Placas", unitSingular: "placa", coverage: 2.97, note: "Cada placa tipo mármol cubre 2.97 m² (1.22m x 2.44m).", hue: 240 },
  { id: "lambrin-sintetico-wpc", name: "Muro Lambrín Sintético WPC", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 0.609, orientable: true, pieceLength: 2.9, usefulWidth: 0.21, note: "Nota: Cada pieza de Lambrín WPC (Uso Exterior) cubre 0.609 m² de ancho útil por empalme (2.9m x 21cm útiles; 22cm totales).", hue: 40 },
  { id: "lambrin-sintetico-pvc", name: "Muro Lambrín Sintético PVC", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 0.464, orientable: true, pieceLength: 2.9, usefulWidth: 0.16, note: "Nota: Cada pieza de Lambrín PVC (Uso Interior) cubre 0.464 m² de ancho útil por empalme (2.9m x 16cm útiles; 17cm totales).", hue: 45 },
  { id: "muro-deck-sintetico", name: "Muro Deck Sintético", category: "Muro", unit: "Piezas", unitSingular: "pieza", coverage: 0.362, note: "Nota: Cada pieza de Muro Deck Sintético (Uso Exterior) cubre 0.362 m² (2.90m x 13.5cm x 2cm).", hue: 35 },
  { id: "plafon", name: "Techo Plafón Falso", category: "Techo", unit: "Piezas", unitSingular: "pieza", coverage: 1.4, note: "Cada pieza de Plafón cubre 1.4 m² (5.95m de largo, 4.3kg).", hue: 220 },
];

const WHATSAPP_NUMBER = "525512989224";

const HUBSPOT_PORTAL_ID = "47654427";
const HUBSPOT_FORM_ID = "0fd3aa49-8045-435d-b22a-c182223105e5";

/** Valor interno exacto de la opción "producto" en el formulario de HubSpot */
const HUBSPOT_PRODUCT_VALUE = {
  "spc-luxury": "Pisos SPC",
  "deck-tablon-wpc": "Tablón WPC",
  "lvt": "Pisos Vinílicos LVT",
  "piso-deck-sintetico": "Deck Sintético",
  "lambrin-concavo": "Lambrín Cóncavo WPC",
  "bamboo-espejo": "Placa Bamboo Espejo",
  "placa-marmol": "Placa Tipo Mármol",
  "lambrin-sintetico-wpc": "Lambrín de WPC",
  "lambrin-sintetico-pvc": "Lambrín de PVC",
  "muro-deck-sintetico": "Muro Deck Sintético",
  "plafon": "Plafón Falso",
};

const IMG_BASE = process.env.PUBLIC_URL + "/assets/img/calculadora";

const PRODUCT_IMAGES = {
  "spc-luxury": `${IMG_BASE}/piso-spc-luxury.webp`,
  "deck-tablon-wpc": `${IMG_BASE}/tablon-deck-wpc.webp`,
  "lvt": `${IMG_BASE}/piso-lvt.webp`,
  "piso-deck-sintetico": `${IMG_BASE}/piso-wpc-deck.webp`,
  "lambrin-concavo": `${IMG_BASE}/lambrin-concavo.webp`,
  "bamboo-espejo": `${IMG_BASE}/muro-bamboo-espejo.webp`,
  "placa-marmol": `${IMG_BASE}/placa-tipo-rigida-marmol.webp`,
  "lambrin-sintetico-wpc": `${IMG_BASE}/lambrin-muro-wpc.webp`,
  "lambrin-sintetico-pvc": `${IMG_BASE}/lambrin-muro-pvc.webp`,
  "muro-deck-sintetico": `${IMG_BASE}/muro-deck-sintetico.webp`,
  "plafon": `${IMG_BASE}/plafon-falso-pvc.webp`,
};

const CATEGORY_FILTERS = [
  { id: "pisos-decks", label: "Pisos y Decks", matches: ["Pisos", "Deck"] },
  { id: "muros-placas", label: "Muros y Placas", matches: ["Muro"] },
  { id: "techos-estructura", label: "Techos", matches: ["Techo"] },
];

// ---------------------------------------------------------------------------
// Mensajes de error centralizados
// ---------------------------------------------------------------------------
const ERRORS = {
  NO_PRODUCT: "Por favor, selecciona un producto.",
  NO_WALL_WIDTH: "Por favor, ingresa el ancho del muro en metros.",
  NO_WALL_HEIGHT: "Por favor, ingresa la altura del muro en metros.",
  NO_AREA: "Por favor, ingresa el área total de tu espacio en m².",
  NO_DIMENSIONS: "Por favor, ingresa el largo y ancho de tu espacio.",
  NO_RESULT: "Primero calcula tu material.",
  NO_NAME: "Por favor, ingresa tu nombre.",
  INVALID_PHONE: "Por favor, ingresa un número de WhatsApp válido a 10 dígitos.",
  INVALID_EMAIL: "Por favor, ingresa un correo electrónico válido.",
  HUBSPOT_FAIL: "No pudimos registrar tu solicitud en este momento. Te redirigiremos a WhatsApp para continuar.",
};

// ---------------------------------------------------------------------------
// Funciones puras de cálculo y validación
// ---------------------------------------------------------------------------
function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function parsePositive(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Calcula la cantidad de material.
 * Devuelve null si faltan datos mínimos.
 */
function calculateResult(product, options) {
  if (!product) return null;

  const { waste, computedArea, wallWidth, wallHeight } = options;
  const factor = waste === "with" ? 1.05 : 1.0;

  // Productos orientables (lambrines): se calculan por columnas × piezas por columna
  if (product.orientable) {
    const W = parsePositive(wallWidth);
    const H = parsePositive(wallHeight);
    if (W <= 0 || H <= 0) return null;

    // Instalación vertical: el ancho útil cubre el ancho del muro;
    // el largo de la pieza recorre la altura (sobrante = merma de corte).
    const runs = Math.ceil((W * factor) / product.usefulWidth);
    const perRun = Math.ceil(H / product.pieceLength);
    const quantity = runs * perRun;
    const acquiredArea = quantity * product.usefulWidth * product.pieceLength;

    return {
      quantity,
      product,
      baseArea: W * H,
      wasteApplied: waste,
      isOrientable: true,
      wallW: W,
      wallH: H,
      runs,
      perRun,
      areaLabel: `Material a adquirir (incluye corte de largo${waste === "with" ? " + 5%" : ""})`,
      areaValue: acquiredArea,
    };
  }

  // Pisos y productos por cobertura
  if (computedArea <= 0) return null;

  const totalWithWaste = computedArea * factor;
  const quantity = Math.ceil(totalWithWaste / product.coverage);

  return {
    quantity,
    product,
    baseArea: computedArea,
    wasteApplied: waste,
    isOrientable: false,
    areaLabel: `Área total requerida ${waste === "with" ? "(con 5% de merma)" : "(medida exacta)"}`,
    areaValue: totalWithWaste,
  };
}

/**
 * Valida los inputs del Paso 02 según el tipo de producto.
 * Devuelve un mensaje de error o null si todo está correcto.
 */
function validateInputs(product, mode, area, length, width, wallWidth, wallHeight) {
  if (!product) return ERRORS.NO_PRODUCT;

  if (product.orientable) {
    if (parsePositive(wallWidth) <= 0) return ERRORS.NO_WALL_WIDTH;
    if (parsePositive(wallHeight) <= 0) return ERRORS.NO_WALL_HEIGHT;
    return null;
  }

  if (mode === "area") {
    if (parsePositive(area) <= 0) return ERRORS.NO_AREA;
    return null;
  }

  if (parsePositive(length) <= 0 || parsePositive(width) <= 0) {
    return ERRORS.NO_DIMENSIONS;
  }
  return null;
}

/**
 * Construye el mensaje prellenado de WhatsApp.
 */
function buildWhatsAppMessage(result, leadName) {
  const base =
    `Hola Decototal, usé la calculadora de su web. Me interesa cotizar este producto: *${result.product.name}*. ` +
    `Área requerida: *${result.baseArea.toFixed(2)} m²*.`;

  const orientablePart = result.isOrientable
    ? ` Muro: *${result.wallW.toFixed(2)} × ${result.wallH.toFixed(2)} m*.`
    : "";

  const quantityPart = ` El resultado del cálculo indica que necesito adquirir: *${result.quantity} ${result.product.unit}*.`;
  const namePart = leadName ? ` Mi nombre es *${leadName}*.` : "";

  return base + orientablePart + quantityPart + namePart;
}

/**
 * Envía el lead a HubSpot.
 * Devuelve { ok: boolean, status?: number, body?: string }
 */
function getHubspotutkCookie() {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/);
  return match ? match[1] : undefined;
}

async function submitToHubSpot({ name, email, phone, productId, productName, detalle }) {
  const url = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`;

  const hutk = getHubspotutkCookie();

  const payload = {
    fields: [
      { name: "firstname", value: name },
      { name: "email", value: email },
      { name: "phone", value: phone },
      { name: "producto", value: HUBSPOT_PRODUCT_VALUE[productId] || productName },
      { name: "message", value: detalle },
    ],
    context: {
      pageUri: typeof window !== "undefined" ? window.location.href : "",
      pageName: typeof document !== "undefined" ? document.title : "",
      ...(hutk ? { hutk } : {}),
    },
  };

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const bodyText = await res.text();

    if (!res.ok) {
      console.error("[HubSpot] Envío rechazado", {
        status: res.status,
        body: bodyText,
        payload,
      });
      return { ok: false, status: res.status, body: bodyText };
    }

    let parsedBody = null;
    try {
      parsedBody = JSON.parse(bodyText);
    } catch (_) { }

    if (parsedBody && Array.isArray(parsedBody.errors) && parsedBody.errors.length > 0) {
      console.warn("[HubSpot] Envío 200 pero con errores por campo", {
        errors: parsedBody.errors,
        payload,
      });
    }

    return { ok: true, status: res.status, body: bodyText };
  } catch (err) {
    console.error("[HubSpot] Error de red o excepción", err);
    return { ok: false, error: err };
  }
}

// ---------------------------------------------------------------------------
// Miniatura de producto
// ---------------------------------------------------------------------------
function ProductThumb({ product, size = "md" }) {
  const [failed, setFailed] = useState(false);
  const imageUrl = PRODUCT_IMAGES[product.id];
  const showImage = imageUrl && !failed;

  return (
    <div
      className={`dc-thumb ${size === "sm" ? "dc-thumb--sm" : "dc-thumb--md"}`}
      style={
        showImage
          ? undefined
          : {
            background: `linear-gradient(135deg, oklch(0.78 0.08 ${product.hue}), oklch(0.55 0.12 ${product.hue}))`,
          }
      }
      aria-hidden="true"
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt={product.name}
          className="dc-thumb-img"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="dc-thumb-pattern" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export function MaterialCalculator() {
  const [productId, setProductId] = useState(PRODUCTS[0].id);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [mode, setMode] = useState("area"); // "area" | "dim"
  const [waste, setWaste] = useState("with"); // "with" | "without"
  const [area, setArea] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [wallWidth, setWallWidth] = useState("");
  const [wallHeight, setWallHeight] = useState("");
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [formError, setFormError] = useState("");
  const [calcError, setCalcError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hubspotWarning, setHubspotWarning] = useState(""); // feedback no bloqueante

  const product = useMemo(
    () => PRODUCTS.find((p) => p.id === productId) || null,
    [productId]
  );

  const filteredProducts = useMemo(() => {
    if (!categoryFilter) return PRODUCTS;
    const f = CATEGORY_FILTERS.find((c) => c.id === categoryFilter);
    if (!f) return PRODUCTS;
    return PRODUCTS.filter((p) => f.matches.includes(p.category));
  }, [categoryFilter]);

  const computedArea = useMemo(() => {
    if (mode === "area") return parsePositive(area);
    return parsePositive(length) * parsePositive(width);
  }, [mode, area, length, width]);

  // Resultado en vivo
  const result = useMemo(
    () =>
      calculateResult(product, {
        waste,
        computedArea,
        wallWidth,
        wallHeight,
      }),
    [product, waste, computedArea, wallWidth, wallHeight]
  );

  // ¿Los datos del Paso 02 están completos?
  const inputsReady = useMemo(() => {
    return validateInputs(product, mode, area, length, width, wallWidth, wallHeight) === null;
  }, [product, mode, area, length, width, wallWidth, wallHeight]);

  const handleCategoryClick = useCallback((catId) => {
    setCategoryFilter((current) => (current === catId ? "" : catId));
  }, []);

  const handleCalculate = useCallback(() => {
    setCalcError("");
    setHubspotWarning("");

    const error = validateInputs(product, mode, area, length, width, wallWidth, wallHeight);
    if (error) {
      setCalcError(error);
      return;
    }

    // Scroll suave al resultado
    setTimeout(() => {
      const el = document.getElementById("result-card");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [product, mode, area, length, width, wallWidth, wallHeight]);

  const whatsappUrl = result
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      buildWhatsAppMessage(result, leadName)
    )}`
    : "#";

  const handleLeadSubmit = useCallback(async () => {
    setFormError("");
    setHubspotWarning("");

    if (!result) {
      setFormError(ERRORS.NO_RESULT);
      return;
    }

    const name = leadName.trim();
    const email = leadEmail.trim();
    const phone = leadPhone.replace(/\D/g, "");

    if (!name) {
      setFormError(ERRORS.NO_NAME);
      return;
    }
    if (!isValidEmail(email)) {
      setFormError(ERRORS.INVALID_EMAIL);
      return;
    }
    if (phone.length !== 10) {
      setFormError(ERRORS.INVALID_PHONE);
      return;
    }

    const detalle =
      `Cotización desde la calculadora. Producto: ${result.product.name}. ` +
      `Área: ${result.baseArea.toFixed(2)} m². Cantidad: ${result.quantity} ${result.product.unit}.` +
      (result.isOrientable
        ? ` Muro: ${result.wallW.toFixed(2)} × ${result.wallH.toFixed(2)} m (instalación vertical).`
        : "");

    setSubmitting(true);

    try {
      const hsResult = await submitToHubSpot({
        name,
        email,
        phone,
        productId: result.product.id,
        productName: result.product.name,
        detalle,
      });

      if (!hsResult.ok) {
        // No bloqueamos al usuario: mostramos aviso y seguimos a WhatsApp
        setHubspotWarning(ERRORS.HUBSPOT_FAIL);
        console.warn("[Calculadora] HubSpot falló, se abre WhatsApp de todas formas", hsResult);
      }
    } catch (err) {
      // Por si acaso (aunque submitToHubSpot ya captura)
      setHubspotWarning(ERRORS.HUBSPOT_FAIL);
      console.error("[Calculadora] Error inesperado en submit", err);
    } finally {
      // dataLayer (si existe)
      const dataLayer = window.dataLayer;
      if (dataLayer) {
        dataLayer.push({
          event: "calculadora_conversion",
          formFormat: "hubspot_whatsapp",
          productName: result.product.name,
          calculatedArea: `${result.baseArea.toFixed(2)} m²`,
          calculatedUnits: `${result.quantity} ${result.product.unit}`,
        });
      }

      window.open(whatsappUrl, "_blank");
      setSubmitting(false);
    }
  }, [result, leadName, leadEmail, leadPhone, whatsappUrl]);

  return (
    <div className="deco-calc">
      {/* Hero */}
      <section className="dc-hero">
        <div className="dc-badge">Calculadora de Material</div>
        <h1 className="dc-hero-title">
          Calcula lo justo. <br />
          <span className="dc-gold-text">Invierte con precisión.</span>
        </h1>
        <p className="dc-hero-sub">
          Estima al instante cuántas cajas, piezas o placas necesitas para tu proyecto, con el margen de merma que elijas.
        </p>
      </section>

      {/* Card principal */}
      <section className="dc-main">
        <div className="dc-card">
          <div className="dc-grid">
            {/* Formulario */}
            <div className="dc-form">
              {/* Paso 01 */}
              <div className="dc-step-head">
                <div className="dc-step-num">Paso 01</div>
                <h2 className="dc-step-title">Selecciona tu producto</h2>
              </div>

              <div className="dc-cats" role="tablist" aria-label="Filtrar productos por categoría">
                {CATEGORY_FILTERS.map((cat) => {
                  const active = cat.id === categoryFilter;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => handleCategoryClick(cat.id)}
                      className={`dc-cat-btn ${active ? "is-active" : ""}`}
                    >
                      {cat.label}
                    </button>
                  );
                })}
              </div>

              <div key={categoryFilter} className="dc-products dc-fade-in">
                {filteredProducts.map((p) => {
                  const active = p.id === productId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProductId(p.id)}
                      className={`dc-product ${active ? "is-active" : ""}`}
                    >
                      <ProductThumb product={p} />
                      <div className="dc-product-name">{p.name}</div>
                    </button>
                  );
                })}
              </div>

              {/* Paso 02 */}
              <div className="dc-step-head dc-step-head--gap">
                <div className="dc-step-num">Paso 02</div>
                <h2 className="dc-step-title">
                  {product?.orientable ? "Mide tu muro" : "Ingresa tu área"}
                </h2>
              </div>

              {product?.orientable ? (
                <>
                  <div className="dc-dim-grid dc-inputs">
                    <div>
                      <label htmlFor="dc-wall-width" className="dc-label">
                        Ancho del muro
                      </label>
                      <div className="dc-input-wrap">
                        <input
                          id="dc-wall-width"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          placeholder="Ej. 3.00"
                          value={wallWidth}
                          onChange={(e) => setWallWidth(e.target.value)}
                          className="dc-input"
                        />
                        <span className="dc-input-suffix">m</span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="dc-wall-height" className="dc-label">
                        Altura del muro
                      </label>
                      <div className="dc-input-wrap">
                        <input
                          id="dc-wall-height"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          placeholder="Ej. 2.40"
                          value={wallHeight}
                          onChange={(e) => setWallHeight(e.target.value)}
                          className="dc-input"
                        />
                        <span className="dc-input-suffix">m</span>
                      </div>
                    </div>
                  </div>
                  <p className="dc-step-desc" style={{ marginTop: "0.75rem" }}>
                    Instalación vertical (de piso a techo). Los recortes de este producto no se
                    reutilizan; por eso el cálculo usa las medidas del muro para estimar las piezas.
                  </p>
                </>
              ) : (
                <>
                  <div className="dc-toggle">
                    {["area", "dim"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMode(m)}
                        className={`dc-toggle-btn ${mode === m ? "is-active" : ""}`}
                      >
                        {m === "area" ? "Área total (m²)" : "Largo × Ancho"}
                      </button>
                    ))}
                  </div>

                  <div className="dc-inputs">
                    {mode === "area" ? (
                      <div>
                        <label htmlFor="dc-area" className="dc-label">
                          Superficie total
                        </label>
                        <div className="dc-input-wrap">
                          <input
                            id="dc-area"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            placeholder="Ej. 25"
                            value={area}
                            onChange={(e) => setArea(e.target.value)}
                            className="dc-input"
                          />
                          <span className="dc-input-suffix">m²</span>
                        </div>
                      </div>
                    ) : (
                      <div className="dc-dim-grid">
                        <div>
                          <label htmlFor="dc-length" className="dc-label">
                            Largo
                          </label>
                          <div className="dc-input-wrap">
                            <input
                              id="dc-length"
                              type="number"
                              inputMode="decimal"
                              min="0"
                              placeholder="0.0"
                              value={length}
                              onChange={(e) => setLength(e.target.value)}
                              className="dc-input"
                            />
                            <span className="dc-input-suffix">m</span>
                          </div>
                        </div>
                        <div>
                          <label htmlFor="dc-width" className="dc-label">
                            Ancho
                          </label>
                          <div className="dc-input-wrap">
                            <input
                              id="dc-width"
                              type="number"
                              inputMode="decimal"
                              min="0"
                              placeholder="0.0"
                              value={width}
                              onChange={(e) => setWidth(e.target.value)}
                              className="dc-input"
                            />
                            <span className="dc-input-suffix">m</span>
                          </div>
                        </div>
                        {computedArea > 0 && (
                          <div className="dc-area-calc">
                            Área calculada:{" "}
                            <span className="dc-bold">{computedArea.toFixed(2)} m²</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Paso 03 */}
              <div className="dc-step-head dc-step-head--gap">
                <div className="dc-step-num">Paso 03</div>
                <h2 className="dc-step-title">Margen de desperdicio</h2>
                <p className="dc-step-desc">
                  Elige si deseas incluir un margen de seguridad para cortes y ajustes.
                </p>
              </div>

              <div className="dc-waste">
                {[
                  { id: "with", title: "Con Merma", sub: "+5% recomendado" },
                  { id: "without", title: "Sin Merma", sub: "Medida exacta" },
                ].map((opt) => {
                  const active = waste === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setWaste(opt.id)}
                      className={`dc-waste-btn ${active ? "is-active" : ""}`}
                    >
                      <span className={`dc-radio ${active ? "is-active" : ""}`}>
                        {active && <span className="dc-radio-dot" />}
                      </span>
                      <span>
                        <span className="dc-waste-title">{opt.title}</span>
                        <span className="dc-waste-sub">{opt.sub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {calcError && (
                <div role="alert" className="dc-calc-error">
                  {calcError}
                </div>
              )}

              <button
                type="button"
                onClick={handleCalculate}
                className="dc-calc-btn"
                disabled={!inputsReady}
                aria-disabled={!inputsReady}
              >
                Calcular Material
              </button>
            </div>

            {/* Resultado */}
            <div id="result-card" className="dc-result">
              <div className="dc-result-glow" />
              <div className="dc-result-inner">
                <div className="dc-result-eyebrow">Resultado</div>
                <h2 className="dc-result-title">Tu cálculo</h2>

                {!result ? (
                  <div className="dc-result-empty">
                    Selecciona un producto e ingresa la superficie de tu proyecto para ver el desglose recomendado.
                  </div>
                ) : (
                  <div className="dc-result-body">
                    <div className="dc-result-product">
                      <div className="dc-result-thumb">
                        <ProductThumb product={result.product} size="sm" />
                      </div>
                      <div className="dc-result-product-meta">
                        <div className="dc-result-product-eyebrow">Producto seleccionado</div>
                        <div className="dc-result-product-name">{result.product.name}</div>
                      </div>
                    </div>

                    <div className="dc-qty">
                      <div className="dc-qty-label">Cantidad a comprar</div>
                      <div className="dc-qty-value">
                        <span className="dc-qty-number">{result.quantity}</span>
                        <span className="dc-qty-unit">{result.product.unit}</span>
                      </div>
                    </div>

                    <div className="dc-area-box">
                      <div className="dc-area-box-label">{result.areaLabel}</div>
                      <div className="dc-area-box-value">
                        {result.areaValue.toFixed(2)}{" "}
                        <span className="dc-area-box-unit">m²</span>
                      </div>
                      {result.isOrientable && (
                        <div className="dc-area-box-label" style={{ marginTop: "0.5rem" }}>
                          Muro: {result.wallW.toFixed(2)} × {result.wallH.toFixed(2)} m (
                          {result.baseArea.toFixed(2)} m²) · {result.runs} columnas ×{" "}
                          {result.perRun} pza
                        </div>
                      )}
                    </div>

                    <div className="dc-note">
                      <div className="dc-note-label">Especificación técnica</div>
                      <p className="dc-note-text">
                        <svg
                          className="dc-note-icon"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v-4" />
                          <path d="M12 8h.01" />
                        </svg>
                        <span>{result.product.note}</span>
                      </p>
                    </div>

                    {/* Lead */}
                    <div className="dc-lead">
                      <div className="dc-lead-title">
                        ¡Listo! Déjanos tu nombre y WhatsApp para enviarte tu cotización formal:
                      </div>
                      <input
                        type="text"
                        value={leadName}
                        onChange={(e) => setLeadName(e.target.value)}
                        placeholder="Tu nombre"
                        autoComplete="name"
                        className="dc-lead-input"
                      />
                      <input
                        type="email"
                        value={leadEmail}
                        onChange={(e) => setLeadEmail(e.target.value)}
                        placeholder="Tu correo electrónico"
                        autoComplete="email"
                        className="dc-lead-input"
                      />
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={leadPhone}
                        onChange={(e) =>
                          setLeadPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                        }
                        placeholder="Tu WhatsApp a 10 dígitos"
                        autoComplete="tel"
                        maxLength={10}
                        className="dc-lead-input"
                      />
                      {formError && (
                        <div role="alert" className="dc-lead-error">
                          {formError}
                        </div>
                      )}
                      {hubspotWarning && (
                        <div role="status" className="dc-lead-error" style={{ opacity: 0.9 }}>
                          {hubspotWarning}
                        </div>
                      )}
                    </div>

                    {/* CTA */}
                    <button
                      type="button"
                      onClick={handleLeadSubmit}
                      disabled={submitting}
                      className="dc-wa-btn"
                    >
                      {submitting ? (
                        <>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                            className="dc-spin"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="9"
                              stroke="currentColor"
                              strokeOpacity="0.3"
                              strokeWidth="3"
                            />
                            <path
                              d="M21 12a9 9 0 0 0-9-9"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                            />
                          </svg>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M17.5 14.4c-.3-.1-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.6-1.5-.9-2.1-.2-.5-.5-.5-.7-.5H8c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2 0 1.3.9 2.5 1.1 2.7.1.2 1.9 2.9 4.5 4 .6.3 1.1.4 1.5.5.6.2 1.2.2 1.7.1.5-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3c1.4.8 3 1.2 4.8 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.2.8.8-3.1-.2-.3c-.9-1.4-1.3-3-1.3-4.6 0-4.5 3.7-8.2 8.2-8.2s8.2 3.7 8.2 8.2c0 4.5-3.6 8.2-8.2 8.2z" />
                          </svg>
                          Quiero cotizar ahora por WhatsApp
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <p className="dc-footnote">
          Puedes activar un 5% de merma recomendado para cortes y ajustes. Todas las cantidades se
          redondean a la unidad completa superior.
        </p>
      </section>
    </div>
  );
}

export default MaterialCalculator;